import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import { useFeatures } from './contexts/FeaturesContext'
import { useToast } from './context/ToastContext'
import { setAccessToken } from './services/api'
import Layout from './components/Layout'
import ExtractLoader from './components/ExtractLoader'
import Login from './pages/Login'
import PrivacyPolicy from './pages/PrivacyPolicy'
import PublicSupport from './pages/PublicSupport'
import Dashboard, { DashboardTrendFull, DashboardDrillDown } from './pages/Dashboard'
import Requests from './pages/Requests'
import RequestDetail from './pages/RequestDetail'
import Emails from './pages/Emails'
import EmailDetail from './pages/EmailDetail'
import Documents from './pages/Documents'
import DocumentDetail from './pages/DocumentDetail'
import Activity from './pages/Activity'
import Templates from './pages/Templates'
import TemplateDetail from './pages/TemplateDetail'
import Settings from './pages/Settings'
import Users from './pages/Users'
import Groups from './pages/Groups'
import UsersGroups from './pages/UsersGroups'
import Billing from './pages/Billing'
import Webhooks from './pages/Webhooks'
import OutputDestinations from './pages/OutputDestinations'
import Guide from './pages/Guide'
import WhatsNew from './pages/WhatsNew'
import Learning from './pages/Learning'
import LearningManage from './pages/LearningManage'
import ContactSupport from './pages/ContactSupport'
import Approvals from './pages/Approvals'
import RecycleBin from './pages/RecycleBin'
import { isAdminOrReader, isTemplateManagerOrReader } from './utils/readerAccess'
import { useOpCo } from './context/OpCoContext'


function PrivateRoute({ children }) {
  const { isAuthenticated, isLoading, accessToken } = useAuth()
  const location = useLocation()
  
  // While MSAL is processing a redirect, show a spinner
  if (isLoading) {
    return (
      // Same size and spot as the start-up screen in index.html, so the mark
      // doesn't jump when React takes over; the label sits below it.
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950">
        <div className="relative">
          <ExtractLoader size={64} className="block" />
          <p className="absolute left-1/2 top-full mt-4 -translate-x-1/2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">Signing you in…</p>
        </div>
      </div>
    )
  }
  
  // Not authenticated - save intended destination, then redirect to login
  if (!isAuthenticated || !accessToken) {
    const intendedPath = location.pathname + location.search + location.hash
    // Persist in localStorage so it survives the MSAL redirect round-trip
    if (intendedPath && intendedPath !== '/' && intendedPath !== '/login') {
      localStorage.setItem('xtract_return_url', intendedPath)
    }
    return <Navigate to="/login" state={{ from: intendedPath }} replace />
  }
  
  return children
}

function AdminRoute({ children }) {
  const { user } = useAuth()
  if (!isAdminOrReader(user)) {
    return <Navigate to="/requests" replace />
  }
  return children
}

// Deep links have to be guarded too, not just the nav. connectors starts true
// and only flips once the flags arrive, so a slow fetch never bounces an admin
// who is entitled to the page.
function FeatureRoute({ feature, children }) {
  const features = useFeatures()
  if (features[feature] === false) {
    return <Navigate to="/requests" replace />
  }
  return children
}

// Users & Groups: org admins, plus (OpCo workspaces on) OpCo admins and OpCo
// managers, who only get the OpCos tab there (see pages/UsersGroups.jsx).
function UsersGroupsRoute({ children }) {
  const { user } = useAuth()
  const { enabled, isOpcoAdmin, isManager } = useOpCo()
  if (!isAdminOrReader(user) && !(enabled && (isOpcoAdmin || isManager))) {
    return <Navigate to="/requests" replace />
  }
  return children
}

function ManagerRoute({ children }) {
  const { user } = useAuth()
  if (!isTemplateManagerOrReader(user)) {
    return <Navigate to="/requests" replace />
  }
  return children
}

/**
 * LoginRedirector - after successful login, redirect to the intended page
 * (saved before the MSAL redirect round-trip) instead of always going to /.
 */
function LoginRedirector() {
  const location = useLocation()

  // Check route state first (if login happened without full page reload),
  // then fall back to localStorage (survives the MSAL redirect round-trip).
  const from = location.state?.from
  const savedUrl = localStorage.getItem('xtract_return_url')
  const returnTo = from || savedUrl || '/'

  // Clean up - don't want stale return URLs
  localStorage.removeItem('xtract_return_url')

  return <Navigate to={returnTo} replace />
}

function App() {
  const { accessToken, sessionExpired, clearSessionExpired, isAuthenticated, accountBlocked, logout } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  
  // Sync access token from auth context → API layer (including clearing on logout)
  useEffect(() => {
    setAccessToken(accessToken || null)
  }, [accessToken])

  // When session expires (401 from backend), show a toast and redirect to login
  useEffect(() => {
    if (sessionExpired) {
      toast.warning('Session Expired', 'Your session has expired. Please sign in again.')
      clearSessionExpired()
      navigate('/login', { replace: true })
    }
  }, [sessionExpired, clearSessionExpired, navigate, toast])

  // Global listener: show toast when reader attempts a blocked action (403 read-only)
  useEffect(() => {
    const handler = (e) => {
      toast.error(e.detail?.message || 'Your account has read-only access.')
    }
    window.addEventListener('reader-blocked', handler)
    return () => window.removeEventListener('reader-blocked', handler)
  }, [toast])

  // Global listener: a template field change put a connector's Map Payload
  // out of sync (dispatched by the api.js response interceptor). Publishing to
  // that connector is blocked until its Map Payload is updated.
  useEffect(() => {
    const handler = (e) => {
      const impact = e.detail?.impact || []
      const names = impact.map((i) => i.name || `Connector ${i.webhook_id}`).join(', ')
      const target = impact.length === 1 ? 'this connector' : 'these connectors'
      toast.warning(
        'Connector mapping out of sync',
        `${names}: the Map Payload no longer matches this template's fields. ` +
          `Update it on the template's Connectors tab — publishing to ${target} is blocked until then.`,
      )
    }
    window.addEventListener('mapping-impact', handler)
    return () => window.removeEventListener('mapping-impact', handler)
  }, [toast])

  // Account deactivated/blocked - show full-page message
  if (accountBlocked) {
    const isProvisioningError = accountBlocked.includes('not been provisioned') || accountBlocked.includes('Marketplace')
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
        <div className="max-w-md w-full text-center">
          <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-white dark:bg-gray-900 shadow-lg p-8">
            <div className="mx-auto w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{accountBlocked}</p>
            {isProvisioningError && (
              <a
                href="https://azuremarketplace.microsoft.com/en-us/marketplace/apps/synapxai1737498498498.xtract_ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors mb-3"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
                Get it on Azure Marketplace
              </a>
            )}
            <div>
              <button
                onClick={logout}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <Routes>
      {/* Public routes (no auth required) */}
      <Route path="/login" element={
        isAuthenticated && accessToken
          ? <LoginRedirector />
          : <Login />
      } />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/support" element={<PublicSupport />} />

      {/* Protected routes with Layout wrapper */}
      <Route path="/" element={
        <PrivateRoute>
          <Layout />
        </PrivateRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="dashboard/trend"     element={<DashboardTrendFull />} />
        <Route path="dashboard/drilldown" element={<DashboardDrillDown />} />
        <Route path="requests" element={<Requests />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="requests/:id" element={<RequestDetail />} />
        <Route path="activity" element={<AdminRoute><Activity /></AdminRoute>} />
        <Route path="templates" element={<ManagerRoute><Templates /></ManagerRoute>} />
        <Route path="templates/:id" element={<ManagerRoute><TemplateDetail /></ManagerRoute>} />
        <Route path="emails" element={<Emails />} />
        <Route path="emails/:id" element={<EmailDetail />} />
        <Route path="documents" element={<Documents />} />
        <Route path="documents/:id" element={<DocumentDetail />} />
        <Route path="webhooks" element={<FeatureRoute feature="connectors"><AdminRoute><Webhooks /></AdminRoute></FeatureRoute>} />
        <Route path="output-destinations" element={<FeatureRoute feature="connectors"><AdminRoute><OutputDestinations /></AdminRoute></FeatureRoute>} />
        <Route path="users-groups" element={<UsersGroupsRoute><UsersGroups /></UsersGroupsRoute>} />
        <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
        <Route path="groups" element={<AdminRoute><Groups /></AdminRoute>} />
        <Route path="billing" element={<AdminRoute><Billing /></AdminRoute>} />
        <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="guide" element={<Guide />} />
        <Route path="whats-new" element={<WhatsNew />} />
        <Route path="learning" element={<Learning />} />
        <Route path="learning/manage" element={<LearningManage />} />
        <Route path="learning/:itemId" element={<Learning />} />
        <Route path="contact-support" element={<ContactSupport />} />
        <Route path="recycle-bin" element={<AdminRoute><RecycleBin /></AdminRoute>} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  )
}

export default App
